/**
 * Full-screen viewer for PDP gallery images.
 *
 * Each image slide renders an .image-zoom-open button (see
 * snippets/product-media.liquid). Clicking it opens the image in a
 * full-screen overlay that works as a slider: arrows and arrow keys on
 * desktop, swipe on mobile, with a position counter. Clicking the image
 * zooms into that point and allows panning; swipe navigation is paused
 * while zoomed so panning doesn't change slides.
 *
 * Listeners are delegated at document level so they survive the
 * [data-variant-featured-media] outerHTML swaps performed by variants.js
 * on variant/colour changes.
 */

(function () {
  if (window.__one8ImageZoom) return;
  window.__one8ImageZoom = true;

  var SWIPE_DISTANCE = 50;

  var modal = null;
  var modalImg = null;
  var modalScroller = null;
  var modalCounter = null;

  var items = [];
  var currentIndex = 0;
  var swipeStartX = 0;
  var swipeStartY = 0;

  function getModal() {
    if (modal) return modal;

    modal = document.createElement('div');
    modal.className = 'image-zoom-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.hidden = true;
    modal.innerHTML =
      '<button type="button" class="image-zoom-modal__close" aria-label="Close">' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
      '<line x1="5" y1="5" x2="19" y2="19"></line>' +
      '<line x1="19" y1="5" x2="5" y2="19"></line>' +
      '</svg></button>' +
      '<button type="button" class="image-zoom-modal__nav image-zoom-modal__nav--prev" aria-label="Previous image">' +
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<polyline points="15 18 9 12 15 6"></polyline>' +
      '</svg></button>' +
      '<button type="button" class="image-zoom-modal__nav image-zoom-modal__nav--next" aria-label="Next image">' +
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<polyline points="9 18 15 12 9 6"></polyline>' +
      '</svg></button>' +
      '<div class="image-zoom-modal__scroller">' +
      '<img class="image-zoom-modal__img" alt="">' +
      '</div>' +
      '<div class="image-zoom-modal__counter" aria-live="polite"></div>';
    document.body.appendChild(modal);

    modalImg = modal.querySelector('.image-zoom-modal__img');
    modalScroller = modal.querySelector('.image-zoom-modal__scroller');
    modalCounter = modal.querySelector('.image-zoom-modal__counter');

    modal
      .querySelector('.image-zoom-modal__close')
      .addEventListener('click', closeModal);
    modal
      .querySelector('.image-zoom-modal__nav--prev')
      .addEventListener('click', function () {
        goTo(currentIndex - 1);
      });
    modal
      .querySelector('.image-zoom-modal__nav--next')
      .addEventListener('click', function () {
        goTo(currentIndex + 1);
      });

    // Backdrop click closes; clicking the image toggles zoomed pan mode.
    modalScroller.addEventListener('click', function (event) {
      if (event.target === modalImg) {
        toggleModalZoom(event);
      } else {
        closeModal();
      }
    });

    // Swipe navigation (mobile). Disabled while zoomed so panning works.
    modalScroller.addEventListener(
      'touchstart',
      function (event) {
        if (event.touches.length !== 1) return;
        swipeStartX = event.touches[0].clientX;
        swipeStartY = event.touches[0].clientY;
      },
      { passive: true }
    );

    modalScroller.addEventListener(
      'touchend',
      function (event) {
        if (modal.classList.contains('is-zoomed')) return;
        var touch = event.changedTouches[0];
        if (!touch) return;

        var dx = touch.clientX - swipeStartX;
        var dy = touch.clientY - swipeStartY;

        if (Math.abs(dx) > SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy)) {
          goTo(dx < 0 ? currentIndex + 1 : currentIndex - 1);
        }
      },
      { passive: true }
    );

    document.addEventListener('keydown', function (event) {
      if (modal.hidden) return;
      if (event.key === 'Escape') closeModal();
      if (event.key === 'ArrowLeft') goTo(currentIndex - 1);
      if (event.key === 'ArrowRight') goTo(currentIndex + 1);
    });

    return modal;
  }

  function collectItems() {
    var buttons = document.querySelectorAll(
      '[data-variant-featured-media] .image-zoom-open'
    );
    return Array.prototype.map.call(buttons, function (button) {
      return {
        src: button.dataset.zoomSrc,
        alt: button.dataset.zoomAlt || '',
      };
    });
  }

  function goTo(index) {
    if (!items.length) return;

    currentIndex = (index + items.length) % items.length;
    var item = items[currentIndex];

    modal.classList.remove('is-zoomed');
    modalScroller.scrollLeft = 0;
    modalScroller.scrollTop = 0;
    modalImg.src = item.src;
    modalImg.alt = item.alt;
    modalCounter.textContent = currentIndex + 1 + ' / ' + items.length;

    var navHidden = items.length < 2;
    modal
      .querySelectorAll('.image-zoom-modal__nav')
      .forEach(function (button) {
        button.hidden = navHidden;
      });
    modalCounter.hidden = navHidden;

    preload(currentIndex + 1);
    preload(currentIndex - 1);
  }

  function preload(index) {
    if (items.length < 2) return;
    var item = items[(index + items.length) % items.length];
    if (item) new Image().src = item.src;
  }

  function openModal(clickedButton) {
    var el = getModal();

    items = collectItems();
    var buttons = document.querySelectorAll(
      '[data-variant-featured-media] .image-zoom-open'
    );
    var index = Array.prototype.indexOf.call(buttons, clickedButton);

    el.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    goTo(index < 0 ? 0 : index);
    el.querySelector('.image-zoom-modal__close').focus();
  }

  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    modal.classList.remove('is-zoomed');
    modalImg.src = '';
    document.documentElement.style.overflow = '';
  }

  function toggleModalZoom(event) {
    var zoomingIn = !modal.classList.contains('is-zoomed');

    // Remember where the user clicked, as a fraction of the image.
    var rect = modalImg.getBoundingClientRect();
    var fx = (event.clientX - rect.left) / rect.width;
    var fy = (event.clientY - rect.top) / rect.height;

    modal.classList.toggle('is-zoomed', zoomingIn);

    if (zoomingIn) {
      // Centre the clicked point in the viewport after the resize.
      window.requestAnimationFrame(function () {
        modalScroller.scrollLeft =
          modalImg.offsetWidth * fx - modalScroller.clientWidth / 2;
        modalScroller.scrollTop =
          modalImg.offsetHeight * fy - modalScroller.clientHeight / 2;
      });
    }
  }

  document.addEventListener('click', function (event) {
    var button =
      event.target instanceof Element
        ? event.target.closest('.image-zoom-open')
        : null;
    if (!button) return;

    event.preventDefault();
    openModal(button);
  });
})();
